import { getCountries, getUniversityById, getEventsForUniversity, getCountryByCode, getTranslatedEventTitle } from '../data-source';
import { getEventSlug } from '../event-slugs';
import { getUniversitySlugForId, getTemplateStr, getPhraseEntry, uiFb, getCountryNameEntry, getSlugForCountry } from '../i18n';
import { COUNTRY_LIST } from '../constants';
import { HOMEPAGE_FAQS } from './homepage-faqs';
import { SITE_URL, SITE_NAME, brand } from '../brand';
import type { CalendarEvent, CountryCode, University } from '../types';
import type { LongWeekend } from '../long-weekends';
import { getCountryOgImage, getCityOgImage, getUniversityOgImage, getEventOgImage, DEFAULT_OG_IMAGE, toAbsoluteImageUrl } from '../images';
import { findCountry, findCity, buildFaqPageJsonLd } from './utils';
import { getCountrySeoData, getCitySeoData, getUniversitySeoData, getCountryReligionSeoData, getCountryNatureSeoData } from './seo-data';
import { YEAR, SITE_LAUNCH_DATE } from './constants';
import { MONTHS } from '../constants';
import { getLocale } from '../date-format';
import { classifyEventSemester } from '../academic-semester';

/** Country names that read with a definite article ("in the United States"). (almanak-rmap) */
const THE_COUNTRIES = new Set([
  'United States', 'United Kingdom', 'United Arab Emirates', 'Netherlands', 'Philippines',
  'Czech Republic', 'Dominican Republic', 'Central African Republic', 'Bahamas', 'Gambia',
  'Maldives', 'Comoros', 'Seychelles', 'Marshall Islands', 'Solomon Islands',
  'Democratic Republic of the Congo', 'Republic of the Congo', 'Isle of Man',
]);
const withThe = (name: string): string => (THE_COUNTRIES.has(name) ? `the ${name}` : name);
/** "a"/"an" by the following word's leading sound (good-enough heuristic). */
const articleFor = (word: string): string => (/^[aeiou]/i.test(word.trim()) ? 'an' : 'a');
/** Auto-description fallback for an event with no notes (grammar-correct). */
const eventTypePhrase = (type: string): string => type.replace(/_/g, ' ');
function autoEventDescription(title: string, type: string, countryName: string): string {
  const phrase = eventTypePhrase(type);
  return `${title} — ${articleFor(phrase)} ${phrase} in ${withThe(countryName)}.`;
}

/** Derive the JSON-LD encodingFormat from an image URL's extension (almanak-vsll). */
function imageMimeType(url: string): string {
  const u = (url || '').split('?')[0].toLowerCase();
  if (u.endsWith('.jpg') || u.endsWith('.jpeg')) return 'image/jpeg';
  if (u.endsWith('.png')) return 'image/png';
  if (u.endsWith('.avif')) return 'image/avif';
  if (u.endsWith('.gif')) return 'image/gif';
  return 'image/webp';
}

/** Builds a consistent ImageObject with licensing metadata for use in JSON-LD */
export function buildImageObject(
  url: string,
  opts?: { name?: string; caption?: string; representativeOfPage?: boolean },
): Record<string, unknown> {
  return {
    '@type': 'ImageObject',
    url,
    contentUrl: url,
    width: 1200,
    height: 630,
    encodingFormat: imageMimeType(url),
    creditText: `${SITE_NAME} / vacant.day`,
    creator: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
    copyrightNotice: `\u00A9 ${YEAR} ${SITE_NAME}`,
    license: `${SITE_URL}/licenses`,
    acquireLicensePage: `${SITE_URL}/licenses`,
    ...(opts?.name && { name: opts.name }),
    ...(opts?.caption && { caption: opts.caption }),
    ...(opts?.representativeOfPage && { representativeOfPage: true }),
  };
}

/** Build an absolute event URL from an event ID, or undefined if no slug exists */
function eventUrl(eventId: string): string | undefined {
  const slug = getEventSlug(eventId);
  return slug ? `${SITE_URL}/events/${slug}` : undefined;
}

/** Build a PostalAddress object for a university, including optional street/postal/region fields */
export function buildUniversityPostalAddress(uni: { city: string; country: string; streetAddress?: string; postalCode?: string; addressRegion?: string }) {
  const cityName = uni.city.charAt(0).toUpperCase() + uni.city.slice(1);
  return {
    '@type': 'PostalAddress' as const,
    addressLocality: cityName,
    addressCountry: uni.country,
    ...(uni.streetAddress && { streetAddress: uni.streetAddress }),
    ...(uni.postalCode && { postalCode: uni.postalCode }),
    ...(uni.addressRegion && { addressRegion: uni.addressRegion }),
  };
}

/** Shared publisher snippet for CollectionPage JSON-LD */
export function buildPublisherSnippet() {
  return {
    '@type': 'Organization' as const,
    name: SITE_NAME,
    url: SITE_URL,
    logo: { '@type': 'ImageObject' as const, url: brand.ogImage },
  };
}

/** Builds JSON-LD structured data for a country's holiday collection page */
export function buildCountryJsonLd(code: string, lang: string = 'en', slugOverride?: string) {
  const seoData = getCountrySeoData(code as CountryCode, lang);
  if (!seoData) return null;

  const { country, events, title, description } = seoData;
  const canonical = slugOverride ? `${SITE_URL}/${slugOverride}` : seoData.canonical;
  const imageUrl = toAbsoluteImageUrl(getCountryOgImage(findCountry(code) || { code }));

  // The CollectionPage is "<Country> Public Holidays" — its ItemList (and count)
  // must contain public holidays only, not the festivals/sports/weather/
  // observances that also live under the country lens. (almanak-7y78)
  const holidayEvents = events.filter(
    (e: CalendarEvent) => e.type === 'public_holiday' || e.type === 'national_day',
  );

  const topEventNames = holidayEvents
    .slice(0, 3)
    .map((e: CalendarEvent) => e.title)
    .join(', ');

  const useLocal = lang !== 'en';
  const eventItems = holidayEvents.map((event: CalendarEvent, index: number) => {
    const url = eventUrl(event.id);
    return {
      '@type': 'ListItem',
      position: index + 1,
      ...(url ? { url } : {}),
      item: {
        '@type': 'Event',
        name: useLocal && event.titleLocal ? event.titleLocal : event.title,
        ...(useLocal && event.titleLocal ? { alternateName: event.title } : {}),
        startDate: event.dateStart,
        endDate: event.dateEnd,
        // Google Event rich-results recommend eventStatus; public holidays are
        // non-attendable so no eventAttendanceMode (almanak-ysb).
        eventStatus: 'https://schema.org/EventScheduled',
        ...(url ? { url } : {}),
        image: toAbsoluteImageUrl(getEventOgImage(event)),
        description: event.notes?.trim() || autoEventDescription(event.title, event.type, country.name),
        location: {
          '@type': 'Place',
          name: country.name,
          address: {
            '@type': 'PostalAddress',
            addressCountry: code,
            addressLocality: country.name,
          },
        },
      },
    };
  });

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'CollectionPage',
        name: title,
        ...(getTemplateStr('seo.country.jsonld_alternate_name', lang, { country: country.name, year: String(YEAR) })
          ? { alternateName: getTemplateStr('seo.country.jsonld_alternate_name', lang, { country: country.name, year: String(YEAR) }) }
          : {}),
        description,
        url: canonical,
        inLanguage: lang,
        // Annual collection: datePublished = year start (semantically correct for "2026 holidays")
        datePublished: `${YEAR}-01-01`,
        dateModified: new Date().toISOString().slice(0, 10),
        publisher: buildPublisherSnippet(),
        primaryImageOfPage: {
          '@type': 'ImageObject',
          url: imageUrl,
          contentUrl: imageUrl,
          name: getTemplateStr('seo.country.jsonld_image_name', lang, { country: country.name, year: String(YEAR) })
            || `${country.name} ${YEAR} Holiday Calendar`,
          caption: getTemplateStr('seo.country.jsonld_image_caption', lang, { country: country.name, year: String(YEAR), count: String(holidayEvents.length), topEvents: topEventNames ? `, including ${topEventNames}` : '' })
            || `Visual calendar of ${holidayEvents.length} public holidays in ${country.name} for ${YEAR}${topEventNames ? `, including ${topEventNames}` : ''}`,
          width: 1200,
          height: 630,
          encodingFormat: imageMimeType(imageUrl),
          representativeOfPage: true,
          about: { '@type': 'Country', name: country.name },
          creator: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
          creditText: `${SITE_NAME} / vacant.day`,
          copyrightNotice: `\u00A9 ${YEAR} ${SITE_NAME}`,
          license: `${SITE_URL}/licenses`,
          acquireLicensePage: `${SITE_URL}/licenses`,
        },
        mainEntity: {
          '@type': 'ItemList',
          numberOfItems: holidayEvents.length,
          itemListElement: eventItems,
        },
      },
      {
        '@type': 'ImageObject',
        contentUrl: imageUrl,
        creditText: `${SITE_NAME} / vacant.day`,
        creator: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
        copyrightNotice: `\u00A9 ${YEAR} ${SITE_NAME}`,
        license: `${SITE_URL}/licenses`,
        acquireLicensePage: `${SITE_URL}/licenses`,
        name: getTemplateStr('seo.country.jsonld_image_name', lang, { country: country.name, year: String(YEAR) })
          || `${country.name} ${YEAR} Holiday Calendar`,
        caption: getTemplateStr('seo.country.jsonld_image_caption', lang, { country: country.name, year: String(YEAR), count: String(events.length), topEvents: '' })
          || `Visual calendar showing ${events.length} public holidays in ${country.name} for ${YEAR}`,
        description: `Poster-style calendar of national holidays, festivals, and key dates in ${country.name} for ${YEAR}`,
      },
    ],
  };
}

/** Builds JSON-LD structured data for a city calendar page */
export function buildCityJsonLd(cityId: string, lang: string = 'en', slugOverride?: string) {
  const seoData = getCitySeoData(cityId, lang);
  if (!seoData) return null;

  const { city, country, events, title, description } = seoData;
  const canonical = slugOverride ? `${SITE_URL}/${slugOverride}` : seoData.canonical;

  const useLocalCity = lang !== 'en';
  const eventItems = events.map((event: CalendarEvent, index: number) => {
    const url = eventUrl(event.id);
    // Festivals/observances on a city page are attendable; holidays are not
    // (mirrors buildEventJsonLd's rule). Drives eventAttendanceMode (almanak-ysb).
    const isAttendable = !['public_holiday', 'national_day', 'observance'].includes(event.type);
    return {
      '@type': 'ListItem',
      position: index + 1,
      ...(url ? { url } : {}),
      item: {
        '@type': 'Event',
        name: useLocalCity && event.titleLocal ? event.titleLocal : event.title,
        ...(useLocalCity && event.titleLocal ? { alternateName: event.title } : {}),
        startDate: event.dateStart,
        endDate: event.dateEnd,
        eventStatus: 'https://schema.org/EventScheduled',
        ...(isAttendable && { eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode' }),
        ...(url ? { url } : {}),
        image: toAbsoluteImageUrl(getEventOgImage(event)),
        description: event.notes?.trim() || autoEventDescription(event.title, event.type, city.name),
        location: {
          '@type': 'Place',
          name: city.name,
          address: {
            '@type': 'PostalAddress',
            addressLocality: city.name,
            addressCountry: event.country,
          },
        },
      },
    };
  });

  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: title,
    description,
    url: canonical,
    inLanguage: lang,
    datePublished: `${YEAR}-01-01`,
    dateModified: new Date().toISOString().slice(0, 10),
    publisher: buildPublisherSnippet(),
    primaryImageOfPage: buildImageObject(
      toAbsoluteImageUrl(getCityOgImage(findCity(cityId) || { id: cityId })),
      { representativeOfPage: true },
    ),
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: events.length,
      itemListElement: eventItems,
    },
  };
}

/** Stable @id references — let entities link across pages. */
export const ORG_ID = `${SITE_URL}/#organization`;
export const WEBSITE_ID = `${SITE_URL}/#website`;
export const HOMEPAGE_ID = `${SITE_URL}/#webpage`;

/** Builds a unified @graph for the homepage: WebSite + WebPage (with Speakable)
 *  + ItemList of the 14 covered countries + upcoming Events, cross-linked to
 *  Organization. */
export function buildHomepageJsonLd(upcomingEvents: CalendarEvent[] = []) {
  const countryItems = COUNTRY_LIST.map((c, i) => {
    const slug = getSlugForCountry(c.code as CountryCode, 'en');
    return {
      '@type': 'ListItem',
      position: i + 1,
      name: c.label,
      url: slug ? `${SITE_URL}/${slug}` : SITE_URL,
    };
  });

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebSite',
        '@id': WEBSITE_ID,
        name: SITE_NAME,
        url: SITE_URL,
        inLanguage: 'en',
        publisher: { '@id': ORG_ID },
        description: `${brand.tagline}. Explore holidays through cultural, religious, and regional perspectives.`,
        potentialAction: {
          '@type': 'SearchAction',
          target: `${SITE_URL}/?q={search_term_string}`,
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@type': 'WebPage',
        '@id': HOMEPAGE_ID,
        url: `${SITE_URL}/`,
        name: SITE_NAME,
        inLanguage: 'en',
        isPartOf: { '@id': WEBSITE_ID },
        about: { '@id': ORG_ID },
        primaryImageOfPage: {
          '@type': 'ImageObject',
          url: brand.ogImage,
          width: 1200,
          height: 630,
        },
        speakable: {
          '@type': 'SpeakableSpecification',
          cssSelector: ['h1', '#main-content h2'],
        },
        mainEntity: { '@id': `${SITE_URL}/#country-directory` },
      },
      {
        '@type': 'ItemList',
        '@id': `${SITE_URL}/#country-directory`,
        name: 'Countries covered by vacant.day',
        numberOfItems: countryItems.length,
        itemListElement: countryItems,
      },
      {
        '@type': 'FAQPage',
        '@id': `${SITE_URL}/#faq`,
        isPartOf: { '@id': HOMEPAGE_ID },
        inLanguage: 'en',
        mainEntity: HOMEPAGE_FAQS.map((f) => ({
          '@type': 'Question',
          name: f.questionFallback,
          acceptedAnswer: {
            '@type': 'Answer',
            text: f.answerFallback,
          },
        })),
      },
      ...upcomingEvents.slice(0, 8).map((evt) => {
        const country = getCountryByCode(evt.country);
        const slug = getEventSlug(evt.id);
        return {
          '@type': 'Event',
          '@id': `${SITE_URL}/events/${slug}#event`,
          name: evt.title,
          startDate: evt.dateStart,
          endDate: evt.dateEnd,
          eventStatus: 'https://schema.org/EventScheduled',
          eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
          url: slug ? `${SITE_URL}/events/${slug}` : `${SITE_URL}/`,
          location: {
            '@type': 'Country',
            name: country?.name || evt.country,
          },
          isPartOf: { '@id': WEBSITE_ID },
          publisher: { '@id': ORG_ID },
        };
      }),
    ],
  };
}

/** Builds Organization JSON-LD with stable @id so other schemas can reference it. */
export function buildOrganizationJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': ORG_ID,
    name: SITE_NAME,
    url: SITE_URL,
    logo: {
      '@type': 'ImageObject',
      url: brand.ogImage,
      width: 1200,
      height: 630,
    },
    description: brand.description,
    foundingDate: `${brand.foundingYear}-01-01`,
    knowsAbout: ['Public Holidays', 'Cultural Festivals', 'Religious Observances', 'Academic Calendars', 'Travel Planning'],
  };
}

/** Builds schema.org FAQPage JSON-LD auto-generated from event data fields */
export function buildEventFaqJsonLd(event: CalendarEvent, lang?: string) {
  const effectiveLang = lang || 'en';
  const countryName = getCountryNameEntry(event.country as CountryCode, effectiveLang).display;
  const year = event.dateStart.slice(0, 4);
  const isPublicHoliday = ['public_holiday', 'national_day'].includes(event.type);
  const typeLabel = uiFb(`ui.type.${event.type}`, event.type.replace(/_/g, ' '), effectiveLang);
  // Use the page-language translation for the event name (not titleLocal, which is
  // the country-local name and would leak e.g. Arabic on a zh page). (almanak-tgrh)
  const displayName = effectiveLang !== 'en'
    ? getTranslatedEventTitle(event.id, effectiveLang, event.title)
    : event.title;

  const locale = getLocale(effectiveLang);
  const startDate = new Date(event.dateStart + 'T00:00:00');
  const endDate = new Date(event.dateEnd + 'T00:00:00');
  const fmtOpts: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
  const startStr = startDate.toLocaleDateString(locale, fmtOpts);
  const endStr = startDate.getTime() === endDate.getTime()
    ? startStr
    : `${startStr} – ${endDate.toLocaleDateString(locale, fmtOpts)}`;

  const tpl = (key: string, vars: Record<string, string>) =>
    getTemplateStr(key, effectiveLang, vars);

  const ACADEMIC_TYPES = ['school_term_start', 'school_term_end', 'exam_period', 'enrollment_deadline', 'school_holiday'];
  const isAcademicEvent = ACADEMIC_TYPES.includes(event.type);

  const questions: Array<{ name: string; answer: string }> = [];

  // Q1: Context-appropriate first question
  if (isAcademicEvent) {
    // Academic events: ask "What is X?" instead of "Is X a public holiday?"
    const q1 = tpl('seo.event.faq_what_is_q', { event: displayName, country: countryName });
    const academicAnswers: Record<string, string> = {
      school_term_start: `${displayName} marks the beginning of the academic term in ${countryName}. Students should prepare enrollment documents and check class schedules.`,
      school_term_end: `${displayName} marks the end of the academic term in ${countryName}. Final exams and grade submissions typically occur around this date.`,
      exam_period: `${displayName} is an examination period in ${countryName}. Students should prepare study materials and check exam schedules with their institution.`,
      enrollment_deadline: `${displayName} is an enrollment or registration deadline in ${countryName}. Applicants must complete all required submissions before this date.`,
      school_holiday: `${displayName} is a school holiday in ${countryName}. Classes are suspended, though administrative offices may remain open.`,
    };
    const a1 = tpl('seo.event.faq_what_is_a', { event: displayName, type: typeLabel, country: countryName });
    questions.push({
      name: q1 || `What is ${displayName} in ${countryName}?`,
      answer: a1 || academicAnswers[event.type] || `${displayName} is ${/^[aeiou]/i.test(typeLabel) ? 'an' : 'a'} ${typeLabel} in ${countryName}.`,
    });
  } else {
    const q1 = tpl('seo.event.faq_is_public_q', { event: displayName, country: countryName });
    const a1 = isPublicHoliday
      ? tpl('seo.event.faq_is_public_a_yes', { event: displayName, country: countryName })
      : tpl('seo.event.faq_is_public_a_no', { event: displayName, type: typeLabel, country: countryName });
    questions.push({
      name: q1 || `Is ${displayName} a public holiday in ${countryName}?`,
      answer: a1 || (isPublicHoliday
        ? `Yes, ${displayName} is an official public holiday in ${countryName}. Government offices, banks, and most businesses are typically closed.`
        : `${displayName} is ${/^[aeiou]/i.test(typeLabel) ? 'an' : 'a'} ${typeLabel} in ${countryName}, but it is not an official public holiday. Most businesses remain open.`),
    });
  }

  // Q2: Always — when is it?
  const q2 = tpl('seo.event.faq_when_q', { event: displayName, year });
  const a2 = tpl('seo.event.faq_when_a', { event: displayName, year, date: endStr });
  questions.push({
    name: q2 || `When is ${displayName} ${year}?`,
    answer: a2 || `${displayName} ${year} falls on ${endStr}.`,
  });

  // Q3: Business impact or academic preparation
  if (event.businessImpact) {
    const q3 = tpl('seo.event.faq_closed_q', { event: displayName, country: countryName });
    questions.push({
      name: q3 || `What is closed during ${displayName} in ${countryName}?`,
      answer: event.businessImpact,
    });
  } else if (isAcademicEvent) {
    const prepFallbacks: Record<string, string> = {
      school_term_start: `Prepare enrollment documents, check class schedules, and confirm registration status before ${displayName} in ${countryName}.`,
      school_term_end: `Complete all assignments, check final exam schedules, and review grade submission deadlines before ${displayName} in ${countryName}.`,
      exam_period: `Review the exam timetable, gather required materials (ID, stationery), and confirm exam venues for ${displayName} in ${countryName}.`,
      enrollment_deadline: `Gather required documents (transcripts, ID, test scores), complete online forms, and pay fees before the ${displayName} deadline in ${countryName}.`,
      school_holiday: `Plan activities and childcare arrangements during ${displayName} in ${countryName}. Check if school offices remain open for administrative matters.`,
    };
    const q3Prep = tpl('seo.event.faq_prep_q', { event: displayName });
    const a3Prep = tpl(`seo.event.faq_prep_a_${event.type}`, { event: displayName, country: countryName });
    questions.push({
      name: q3Prep || `How should I prepare for ${displayName}?`,
      answer: a3Prep || prepFallbacks[event.type] || `Check official announcements and prepare required documents for ${displayName} in ${countryName}.`,
    });
  }

  // Q4: If titleLocal — local name (skip when the heading already shows it)
  if (displayName !== event.titleLocal && event.titleLocal && event.titleLocal !== event.title) {
    const q4 = tpl('seo.event.faq_local_name_q', { event: event.title, country: countryName });
    const a4 = tpl('seo.event.faq_local_name_a', { event: event.title, localName: event.titleLocal, country: countryName });
    questions.push({
      name: q4 || `What is ${event.title} called locally in ${countryName}?`,
      answer: a4 || `${event.title} is locally known as "${event.titleLocal}" in ${countryName}.`,
    });
  }

  // Q5: If businessImpactLevel — business impact severity
  if (event.businessImpactLevel && event.businessImpact) {
    const severity: Record<string, string> = {
      critical: 'severe disruption to business operations',
      high: 'significant disruption to business operations',
      medium: 'moderate impact on business operations',
      low: 'minimal impact on business operations',
    };
    const severityText = severity[event.businessImpactLevel] || 'some impact on business operations';
    const q5 = tpl('seo.event.faq_business_q', { event: displayName, country: countryName });
    const a5 = tpl('seo.event.faq_business_a', { event: displayName, severity: severityText, country: countryName, impact: event.businessImpact });
    questions.push({
      name: q5 || `How does ${displayName} affect business in ${countryName}?`,
      answer: a5 || `${displayName} causes ${severityText} in ${countryName}. ${event.businessImpact}`,
    });
  }

  return buildFaqPageJsonLd(questions.map(({ name, answer }) => ({ question: name, answer })));
}

/** Builds schema.org Event JSON-LD for an individual event page */
export function buildEventJsonLd(event: CalendarEvent, lang?: string) {
  const country = getCountryByCode(event.country);
  const countryName = country?.name || event.country;
  const slug = getEventSlug(event.id);

  const imageUrl = toAbsoluteImageUrl(getEventOgImage(event));

  // Organizer: university for academic events, government for public holidays
  let organizer: Record<string, unknown> | undefined;
  const university = event.universityId
    ? getUniversityById(event.universityId)
    : undefined;
  if (university) {
    const uniUrl = university.website || university.sourceUrl;
    organizer = {
      '@type': 'Organization',
      name: university.name,
      ...(uniUrl && { url: uniUrl }),
    };
  } else if (['public_holiday', 'national_day'].includes(event.type)) {
    organizer = {
      '@type': 'Organization',
      name: `Government of ${countryName}`,
    };
  }

  // Location: use university Place with full address for academic events
  const location = university
    ? {
        '@type': 'Place' as const,
        name: university.name,
        address: buildUniversityPostalAddress(university),
      }
    : {
        '@type': 'Place' as const,
        name: event.city || countryName,
        address: {
          '@type': 'PostalAddress' as const,
          addressCountry: event.country,
          addressLocality: event.city || countryName,
        },
      };

  const eventUrl = slug
    ? (lang && lang !== 'en' ? `${SITE_URL}/events/${slug}/${lang}` : `${SITE_URL}/events/${slug}`)
    : undefined;

  const useLocal = lang && lang !== 'en' && event.titleLocal && event.titleLocal !== event.title;
  const eventName = useLocal ? event.titleLocal! : event.title;

  // Only include offers/attendanceMode for attendable event types
  const isAttendable = !['public_holiday', 'national_day', 'observance'].includes(event.type);

  return {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: eventName,
    ...(useLocal ? { alternateName: event.title } : {}),
    startDate: event.dateStart,
    endDate: event.dateEnd,
    description: event.notes?.trim() || autoEventDescription(event.title, event.type, countryName),
    ...(eventUrl && { url: eventUrl }),
    ...(lang ? { inLanguage: lang } : {}),
    image: buildImageObject(imageUrl),
    eventStatus: 'https://schema.org/EventScheduled',
    location,
    ...(isAttendable && { eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode' }),
    ...(organizer && { organizer }),
    ...(isAttendable && eventUrl && {
      offers: {
        '@type': 'Offer',
        price: 0,
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
        url: eventUrl,
      },
    }),
    isAccessibleForFree: true,
  };
}

/** Builds a standalone ImageObject JSON-LD for an event's OG image (licensing metadata) */
export function buildEventImageJsonLd(event: CalendarEvent): Record<string, unknown> {
  const imageUrl = toAbsoluteImageUrl(getEventOgImage(event));

  const country = getCountryByCode(event.country);
  const countryName = country?.name || event.country;
  const year = event.dateStart.slice(0, 4);

  return {
    '@context': 'https://schema.org',
    '@type': 'ImageObject',
    contentUrl: imageUrl,
    creditText: `${SITE_NAME} / vacant.day`,
    creator: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
    copyrightNotice: `\u00A9 ${year} ${SITE_NAME}`,
    license: `${SITE_URL}/licenses`,
    acquireLicensePage: `${SITE_URL}/licenses`,
    name: `${event.title} — ${countryName} ${year}`,
    caption: `${event.title} in ${countryName}, ${year}`,
    width: 1200,
    height: 630,
    encodingFormat: imageMimeType(imageUrl),
  };
}

/** Builds JSON-LD structured data for a university calendar page */
/** Event types that schema.org models as EducationalEvent rather than generic Event.
 *  Google Education rich-results + LLM assistants prefer this taxonomy for academic dates.
 *  See https://schema.org/EducationalEvent */
const EDUCATIONAL_EVENT_TYPES = new Set<string>([
  'school_term_start',
  'school_term_end',
  'exam_period',
  'enrollment_deadline',
  'school_holiday',
]);

function eventSchemaType(eventType: string): 'EducationalEvent' | 'Event' {
  return EDUCATIONAL_EVENT_TYPES.has(eventType) ? 'EducationalEvent' : 'Event';
}

export function buildUniversityJsonLd(universityId: string, lang: string = 'en', slugOverride?: string, academicYear?: number) {
  const seoData = getUniversitySeoData(universityId, lang, academicYear);
  if (!seoData) return null;

  const { university: uni, events, title, description } = seoData;
  const canonical = slugOverride ? `${SITE_URL}/${slugOverride}` : seoData.canonical;
  const uniAddress = buildUniversityPostalAddress(uni);

  // Resolve the academic year window (used by Course/CourseInstance dates).
  const eventYears = events.map((e: CalendarEvent) => parseInt(e.dateStart.slice(0, 4), 10)).filter((n: number) => !Number.isNaN(n));
  const acStart = academicYear ?? (eventYears.length ? Math.min(...eventYears) : YEAR);

  const eventItems = events.map((event: CalendarEvent, index: number) => {
    const url = eventUrl(event.id);
    const schemaType = eventSchemaType(event.type);
    return {
      '@type': 'ListItem',
      position: index + 1,
      ...(url ? { url } : {}),
      item: {
        '@type': schemaType,
        // Stable @id so Course → CourseInstance can reference the same event below.
        '@id': url ? `${url}#event` : `${canonical}#event-${event.id}`,
        name: event.title,
        startDate: event.dateStart,
        endDate: event.dateEnd,
        eventStatus: 'https://schema.org/EventScheduled',
        eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
        ...(url ? { url } : {}),
        image: toAbsoluteImageUrl(getEventOgImage(event)),
        description: event.notes?.trim() || `${event.title} — ${uni.name}.`,
        location: {
          '@type': 'Place',
          name: uni.name,
          address: uniAddress,
        },
        // For EducationalEvent, link organizer to the school node so Google can
        // attribute this to the EducationalOrganization in the same @graph.
        ...(schemaType === 'EducationalEvent' ? { organizer: { '@id': `${canonical}#school` } } : {}),
      },
    };
  });

  // Course node represents the academic year as a unit; CourseInstance per
  // semester gives schema.org consumers the term structure. Terms are DERIVED
  // from the actual events (classified by semester) and localized — Indonesian
  // universities run Semester Ganjil (odd, ~Aug–Jan) and Semester Genap (even,
  // ~Feb–Jul), not US Fall/Spring. (almanak-57rz.8, almanak-bghw)
  const courseId = `${canonical}#course-${acStart}-${acStart + 1}`;
  // The Ganjil/Genap gloss is Indonesian; key it off the university's COUNTRY,
  // not the page language, so a non-ID university's English page (e.g. Malaysian
  // MMU) doesn't emit Indonesian terminology in structured data. Mirrors the
  // KeyDatesTable labels. (almanak-m8lj, almanak-57rz.8)
  const isIndonesianUni = uni.country === 'ID';
  const termName = (odd: boolean) =>
    lang === 'id'
      ? `Semester ${odd ? 'Ganjil' : 'Genap'} ${acStart}/${acStart + 1}`
      : isIndonesianUni
        ? `${odd ? 'Odd' : 'Even'} Semester (${odd ? 'Ganjil' : 'Genap'}) ${acStart}/${acStart + 1}`
        : `${odd ? 'First' : 'Second'} Semester ${acStart}/${acStart + 1}`;
  const buildTerm = (evs: CalendarEvent[], odd: boolean) => {
    if (!evs.length) return null;
    const startDate = evs.reduce((m, e) => (e.dateStart < m ? e.dateStart : m), evs[0].dateStart);
    const endDate = evs.reduce((m, e) => {
      const d = e.dateEnd || e.dateStart;
      return d > m ? d : m;
    }, evs[0].dateEnd || evs[0].dateStart);
    return {
      '@type': 'CourseInstance',
      '@id': `${canonical}#sem-${odd ? 'odd' : 'even'}-${odd ? acStart : acStart + 1}`,
      name: `${uni.name} — ${termName(odd)}`,
      startDate,
      endDate,
      courseMode: 'onsite',
      location: { '@type': 'Place', name: uni.name, address: uniAddress },
    };
  };
  const courseInstances = [
    buildTerm(events.filter((e: CalendarEvent) => classifyEventSemester(e, acStart) === 'odd'), true),
    buildTerm(events.filter((e: CalendarEvent) => classifyEventSemester(e, acStart) === 'even'), false),
  ].filter(Boolean);
  const courseNode = {
    '@type': 'Course',
    '@id': courseId,
    name: `${uni.name} — ${acStart}/${acStart + 1} Academic Year`,
    description: `Academic-year course structure for ${uni.name}, including semester start dates, exam periods, and graduation.`,
    provider: { '@id': `${canonical}#school` },
    hasCourseInstance: courseInstances,
  };

  const uniImageUrl = toAbsoluteImageUrl(getUniversityOgImage(uni));

  // Stable @id values for cross-referencing inside the @graph.
  // Pattern matches the homepage @graph (WebSite/WebPage/ItemList all have @id).
  const schoolId = `${canonical}#school`;
  const webpageId = `${canonical}#webpage`;
  const itemListId = `${canonical}#academic-events`;

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'EducationalOrganization',
        '@id': schoolId,
        name: uni.name,
        alternateName: uni.abbrev,
        url: canonical,
        image: buildImageObject(uniImageUrl, { name: `${uni.name} (${uni.abbrev})` }),
        ...(uni.website ? { sameAs: uni.website } : uni.sourceUrl ? { sameAs: uni.sourceUrl } : {}),
        address: uniAddress,
        // Site publisher reference — the deployed Organization JSON-LD lives at /#organization.
        parentOrganization: { '@id': ORG_ID },
      },
      {
        '@type': 'CollectionPage',
        '@id': webpageId,
        name: title,
        description,
        url: canonical,
        inLanguage: lang,
        datePublished: `${YEAR}-01-01`,
        dateModified: new Date().toISOString().slice(0, 10),
        // Publisher → site Organization (was: buildPublisherSnippet which embeds
        // an inline Organization that doesn't link to the canonical /#organization node).
        publisher: { '@id': ORG_ID },
        primaryImageOfPage: buildImageObject(uniImageUrl, { representativeOfPage: true }),
        // about → reference the EducationalOrganization sibling node via @id
        // (was: an anonymous EducationalOrganization stub with just {name}).
        about: { '@id': schoolId },
        mainEntity: { '@id': itemListId },
        // Speakable (almanak-uj7qp.5): mark H1 + FAQ section so AI assistants
        // can extract a clean spoken answer to "When is UPLB's first semester?".
        speakable: {
          '@type': 'SpeakableSpecification',
          cssSelector: ['h1', '#faq', '[data-speakable]'],
        },
        // Provenance (almanak-uj7qp.11): link the page back to the registrar's
        // official academic-calendar publication so AI engines can cite us
        // confidently. Only emitted when the underlying university has a
        // recorded sourceUrl (Office of University Registrar PDF / page).
        ...(uni.sourceUrl ? {
          isBasedOn: {
            '@type': 'CreativeWork',
            url: uni.sourceUrl,
            name: `${uni.name} — Official academic calendar`,
          },
          sourceOrganization: { '@id': schoolId },
        } : {}),
      },
      {
        '@type': 'ItemList',
        '@id': itemListId,
        name: `${uni.name} academic events`,
        numberOfItems: events.length,
        itemListElement: eventItems,
        isPartOf: { '@id': webpageId },
      },
      courseNode,
    ],
  };
}

/** JSON-LD for hub collection pages */
/**
 * ItemList of a country's long weekends — each long weekend as an Event spanning
 * its full date range (not just the anchor holiday), with leave/days-off context.
 * Gives travel queries a machine-readable list. (almanak-zhz2)
 */
export function buildLongWeekendListJsonLd(countryName: string, longWeekends: LongWeekend[], url: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: `${countryName} Long Weekends ${YEAR}`,
    url,
    numberOfItems: longWeekends.length,
    itemListElement: longWeekends.map((lw, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'Event',
        name: `${lw.holiday.title} long weekend`,
        startDate: lw.startDate,
        endDate: lw.endDate,
        eventStatus: 'https://schema.org/EventScheduled',
        eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
        description: lw.leaveDaysNeeded > 0
          ? `A ${lw.totalDaysOff}-day long weekend around ${lw.holiday.title} — take ${lw.leaveDaysNeeded} day${lw.leaveDaysNeeded !== 1 ? 's' : ''} of leave.`
          : `A natural ${lw.totalDaysOff}-day long weekend around ${lw.holiday.title} — no leave needed.`,
        location: { '@type': 'Country', name: countryName },
      },
    })),
  };
}

export function buildHubJsonLd(title: string, description: string, url: string, events: CalendarEvent[]) {
  const countries = getCountries();
  const countryMap = new Map(countries.map(c => [c.code, c.name]));

  const eventItems = events.slice(0, 50).map((event, index) => {
    const url = eventUrl(event.id);
    const countryName = countryMap.get(event.country) || event.country;
    return {
      '@type': 'ListItem',
      position: index + 1,
      ...(url ? { url } : {}),
      item: {
        '@type': 'Event',
        name: event.title,
        startDate: event.dateStart,
        endDate: event.dateEnd,
        ...(url ? { url } : {}),
        image: toAbsoluteImageUrl(getEventOgImage(event)),
        description: event.notes?.trim() || autoEventDescription(event.title, event.type, countryName),
        location: {
          '@type': 'Place',
          name: event.city || countryName,
          address: {
            '@type': 'PostalAddress',
            addressLocality: event.city || countryName,
            addressCountry: event.country,
          },
        },
      },
    };
  });

  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: title,
    description,
    url,
    datePublished: `${YEAR}-01-01`,
    dateModified: new Date().toISOString().slice(0, 10),
    publisher: buildPublisherSnippet(),
    primaryImageOfPage: buildImageObject(toAbsoluteImageUrl(DEFAULT_OG_IMAGE)),
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: events.length,
      itemListElement: eventItems,
    },
  };
}

/** Builds CollectionPage JSON-LD for university hub pages. */
export function buildUniversityHubJsonLd(
  countryName: string,
  countryCode: string,
  universities: Array<{ id: string; name: string; abbrev: string; city: string; students: number; streetAddress?: string; postalCode?: string; addressRegion?: string }>,
  hubSlug: string,
  lang?: string,
) {
  const items = universities.map((uni, i) => {
    const uniSlug = getUniversitySlugForId(uni.id);
    const url = uniSlug ? `${SITE_URL}/${uniSlug}` : undefined;
    return {
      '@type': 'ListItem',
      position: i + 1,
      ...(url ? { url } : {}),
      item: {
        '@type': 'EducationalOrganization',
        name: uni.name,
        alternateName: uni.abbrev,
        ...(url ? { url } : {}),
        address: buildUniversityPostalAddress({ ...uni, country: countryCode }),
      },
    };
  });

  const effectiveLang = lang ?? 'en';
  const phrase = getPhraseEntry('university_calendars', effectiveLang);
  const ldName = (effectiveLang === 'id' || effectiveLang === 'ms')
    ? `${phrase.display} ${countryName} ${YEAR}`
    : `${countryName} ${phrase.display} ${YEAR}`;
  const ldDesc = uiFb(
    'uni.hub_meta_desc',
    `Browse ${YEAR} university academic calendars for ${universities.length} universities in ${countryName}. Find semester start dates, term dates, exam schedules, and graduation ceremonies.`,
    effectiveLang,
    { year: String(YEAR), count: String(universities.length), country: countryName },
  );

  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: ldName,
    description: ldDesc,
    url: `${SITE_URL}/${hubSlug}`,
    datePublished: `${YEAR}-01-01`,
    dateModified: new Date().toISOString().slice(0, 10),
    publisher: buildPublisherSnippet(),
    primaryImageOfPage: buildImageObject(toAbsoluteImageUrl(getCountryOgImage({ code: countryCode })), { representativeOfPage: true }),
    inLanguage: effectiveLang,
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: universities.length,
      itemListElement: items,
    },
  };
}

/** Builds JSON-LD for a country religion page */
export function buildCountryReligionJsonLd(code: string, lang: string = 'en', slugOverride?: string) {
  const seoData = getCountryReligionSeoData(code as CountryCode, lang);
  if (!seoData) return null;

  const { country, events, title, description } = seoData;
  const canonical = slugOverride ? `${SITE_URL}/${slugOverride}` : seoData.canonical;

  const useLocalRel = lang !== 'en';
  const eventItems = events.map((event: CalendarEvent, index: number) => {
    const url = eventUrl(event.id);
    return {
      '@type': 'ListItem',
      position: index + 1,
      ...(url ? { url } : {}),
      item: {
        '@type': 'Event',
        name: useLocalRel && event.titleLocal ? event.titleLocal : event.title,
        ...(useLocalRel && event.titleLocal ? { alternateName: event.title } : {}),
        startDate: event.dateStart,
        endDate: event.dateEnd,
        ...(url ? { url } : {}),
        image: toAbsoluteImageUrl(getEventOgImage(event)),
        description: event.notes?.trim() || autoEventDescription(event.title, event.type, country.name),
        location: {
          '@type': 'Place',
          name: country.name,
          address: { '@type': 'PostalAddress', addressCountry: code, addressLocality: country.name },
        },
      },
    };
  });

  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: title,
    description,
    url: canonical,
    inLanguage: lang,
    datePublished: `${YEAR}-01-01`,
    dateModified: new Date().toISOString().slice(0, 10),
    publisher: buildPublisherSnippet(),
    primaryImageOfPage: buildImageObject(
      toAbsoluteImageUrl(getCountryOgImage(findCountry(code) || { code })),
      { representativeOfPage: true },
    ),
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: events.length,
      itemListElement: eventItems,
    },
  };
}

/** Builds JSON-LD for a country nature page */
export function buildCountryNatureJsonLd(code: string, lang: string = 'en', slugOverride?: string) {
  const seoData = getCountryNatureSeoData(code as CountryCode, lang);
  if (!seoData) return null;

  const { country, events, title, description } = seoData;
  const canonical = slugOverride ? `${SITE_URL}/${slugOverride}` : seoData.canonical;

  const useLocalNat = lang !== 'en';
  const eventItems = events.map((event: CalendarEvent, index: number) => {
    const url = eventUrl(event.id);
    return {
      '@type': 'ListItem',
      position: index + 1,
      ...(url ? { url } : {}),
      item: {
        '@type': 'Event',
        name: useLocalNat && event.titleLocal ? event.titleLocal : event.title,
        ...(useLocalNat && event.titleLocal ? { alternateName: event.title } : {}),
        startDate: event.dateStart,
        endDate: event.dateEnd,
        ...(url ? { url } : {}),
        image: toAbsoluteImageUrl(getEventOgImage(event)),
        description: event.notes?.trim() || autoEventDescription(event.title, event.type, country.name),
        location: {
          '@type': 'Place',
          name: country.name,
          address: { '@type': 'PostalAddress', addressCountry: code, addressLocality: country.name },
        },
      },
    };
  });

  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: title,
    description,
    url: canonical,
    inLanguage: lang,
    datePublished: `${YEAR}-01-01`,
    dateModified: new Date().toISOString().slice(0, 10),
    publisher: buildPublisherSnippet(),
    primaryImageOfPage: buildImageObject(
      toAbsoluteImageUrl(getCountryOgImage(findCountry(code) || { code })),
      { representativeOfPage: true },
    ),
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: events.length,
      itemListElement: eventItems,
    },
  };
}

/** Builds schema.org CollectionPage JSON-LD for a phenomenon page (e.g. Songkran) */
export function buildPhenomenonJsonLd(
  phenomenonTitle: string,
  events: CalendarEvent[],
  countryName: string,
  slug: string,
  countryCode?: string,
) {
  if (events.length === 0) return null;

  const imageUrl = toAbsoluteImageUrl(
    countryCode ? getCountryOgImage(findCountry(countryCode) || { code: countryCode }) : DEFAULT_OG_IMAGE,
  );

  const eventItems = events.map((event, i) => ({
    '@type': 'ListItem' as const,
    position: i + 1,
    item: {
      '@type': 'Event' as const,
      name: event.titleLocal || event.title,
      startDate: event.dateStart,
      endDate: event.dateEnd,
      description: event.notes?.trim() || autoEventDescription(event.title, event.type, countryName),
      image: toAbsoluteImageUrl(getEventOgImage(event)),
      location: {
        '@type': 'Place' as const,
        name: countryName,
        address: {
          '@type': 'PostalAddress' as const,
          addressCountry: countryCode ?? event.country,
          addressLocality: countryName,
        },
      },
      eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    },
  }));

  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: phenomenonTitle,
    description: events[0].notes?.trim() || `${phenomenonTitle} in ${countryName}.`,
    url: `${SITE_URL}/${slug}`,
    inLanguage: 'en',
    datePublished: SITE_LAUNCH_DATE,
    dateModified: new Date().toISOString().slice(0, 10),
    publisher: buildPublisherSnippet(),
    primaryImageOfPage: buildImageObject(imageUrl),
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: events.length,
      itemListElement: eventItems,
    },
  };
}

/** Builds schema.org FAQPage JSON-LD for a phenomenon page */
export function buildPhenomenonFaqJsonLd(
  phenomenonTitle: string,
  events: CalendarEvent[],
  countryName: string,
  year: number,
) {
  const primary = events[0];
  if (!primary) return null;

  const startDate = new Date(primary.dateStart + 'T00:00:00');
  const endDate = new Date(primary.dateEnd + 'T00:00:00');
  const fmtOpts: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
  const startStr = startDate.toLocaleDateString('en-US', fmtOpts);
  const dateRange = startDate.getTime() === endDate.getTime()
    ? startStr
    : `${startStr} to ${endDate.toLocaleDateString('en-US', fmtOpts)}`;

  const isPublicHoliday = primary.type === 'public_holiday' || primary.type === 'national_day';

  const questions: Array<{ name: string; answer: string }> = [
    {
      name: `When is ${phenomenonTitle} ${year}?`,
      answer: `${phenomenonTitle} ${year} is ${dateRange} in ${countryName}.`,
    },
    {
      name: `Is ${phenomenonTitle} a public holiday in ${countryName}?`,
      answer: isPublicHoliday
        ? `Yes, ${phenomenonTitle} is an official public holiday in ${countryName}. Government offices, banks, and most businesses are typically closed.`
        : `${phenomenonTitle} is not an official public holiday in ${countryName}. Most businesses remain open, though tourism areas may be affected.`,
    },
  ];

  if (primary.businessImpact) {
    questions.push({
      name: `How does ${phenomenonTitle} affect business in ${countryName}?`,
      answer: primary.businessImpact,
    });
  }

  return buildFaqPageJsonLd(questions.map(({ name, answer }) => ({ question: name, answer })));
}

/** Builds schema.org TouristDestination JSON-LD for a "Best Time to Visit" page */
export function buildBestTimeJsonLd(
  displayName: string,
  def: { bestMonths: number[]; seasons: Array<{ name: string; tempRange: string }> },
  slug: string,
  climate?: { weatherScore: number; crowdLevel: string; priceLevel: string; tempRange: string } | null,
  countryCode?: string,
): Record<string, unknown> {
  const bestMonthNames = def.bestMonths.map(m => MONTHS[m - 1]).join(', ');

  const jsonLd: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'TouristDestination',
    name: displayName,
    description: `Best time to visit ${displayName}: ${bestMonthNames}. Seasons include ${def.seasons.map(s => `${s.name} (${s.tempRange})`).join(', ')}.`,
    url: `${SITE_URL}/${slug}`,
    touristType: 'Leisure traveler',
    primaryImageOfPage: buildImageObject(
      toAbsoluteImageUrl(countryCode ? getCountryOgImage(findCountry(countryCode) || { code: countryCode }) : DEFAULT_OG_IMAGE),
    ),
  };

  if (climate) {
    jsonLd.climate = {
      '@type': 'PropertyValue',
      name: 'Peak Season Temperature',
      value: climate.tempRange,
    };
  }

  return jsonLd;
}

/** Builds schema.org CollectionPage JSON-LD for a festival index page */
export function buildFestivalIndexJsonLd(
  displayName: string,
  festivalCount: number,
  slug: string,
  year: number,
  events?: CalendarEvent[],
  countryCode?: string,
  lang?: string,
): Record<string, unknown> {
  const useLocal = lang && lang !== 'en';
  const imageUrl = toAbsoluteImageUrl(
    countryCode ? getCountryOgImage(findCountry(countryCode) || { code: countryCode }) : DEFAULT_OG_IMAGE,
  );

  const jsonLd: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${displayName} Festivals ${year}`,
    description: `Complete guide to ${festivalCount} festivals and cultural events in ${displayName} for ${year}.`,
    url: `${SITE_URL}/${slug}`,
    datePublished: SITE_LAUNCH_DATE,
    dateModified: new Date().toISOString().slice(0, 10),
    publisher: buildPublisherSnippet(),
    primaryImageOfPage: buildImageObject(imageUrl),
  };

  if (events && events.length > 0) {
    const itemList = events.slice(0, 50).map((event, index) => {
      const url = eventUrl(event.id);
      return {
        '@type': 'ListItem',
        position: index + 1,
        ...(url ? { url } : {}),
        item: {
          '@type': 'Event',
          name: useLocal && event.titleLocal ? event.titleLocal : event.title,
          ...(useLocal && event.titleLocal ? { alternateName: event.title } : {}),
          startDate: event.dateStart,
          endDate: event.dateEnd,
          ...(url ? { url } : {}),
          image: toAbsoluteImageUrl(getEventOgImage(event)),
          description: event.notes?.trim() || autoEventDescription(event.title, event.type, displayName),
          location: {
            '@type': 'Place',
            name: displayName,
            address: {
              '@type': 'PostalAddress',
              addressCountry: countryCode ?? event.country,
              addressLocality: displayName,
            },
          },
        },
      };
    });

    jsonLd.mainEntity = {
      '@type': 'ItemList',
      numberOfItems: events.length,
      itemListElement: itemList,
    };
  } else {
    jsonLd.numberOfItems = festivalCount;
  }

  return jsonLd;
}

/** Builds schema.org CollectionPage JSON-LD for a country-month page */
export function buildCountryMonthJsonLd(
  displayName: string,
  monthName: string,
  eventCount: number,
  slug: string,
  year: number,
): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${displayName} in ${monthName} ${year}`,
    description: `${eventCount} holidays, festivals, and events in ${displayName} during ${monthName} ${year}.`,
    url: `${SITE_URL}/${slug}`,
    datePublished: SITE_LAUNCH_DATE,
    dateModified: new Date().toISOString().slice(0, 10),
    publisher: buildPublisherSnippet(),
    primaryImageOfPage: buildImageObject(toAbsoluteImageUrl(DEFAULT_OG_IMAGE)),
    numberOfItems: eventCount,
  };
}

/** Builds schema.org TouristDestination JSON-LD for a country-month page */
export function buildTouristDestinationJsonLd(
  displayName: string,
  monthName: string,
  slug: string,
  weatherSummary: string,
  tempRange: string,
  countryCode?: string,
): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'TouristDestination',
    name: `${displayName} in ${monthName}`,
    description: weatherSummary,
    url: `${SITE_URL}/${slug}`,
    touristType: 'Leisure Traveler',
    primaryImageOfPage: buildImageObject(
      toAbsoluteImageUrl(countryCode ? getCountryOgImage(findCountry(countryCode) || { code: countryCode }) : DEFAULT_OG_IMAGE),
    ),
    climate: {
      '@type': 'PropertyValue',
      name: 'Temperature Range',
      value: tempRange,
    },
  };
}

/** Builds schema.org HowTo JSON-LD for planning/guide pages */
export function buildHowToJsonLd(name: string, steps: Array<{ name: string; text: string }>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name,
    step: steps.map((s, i) => ({
      '@type': 'HowToStep',
      position: i + 1,
      name: s.name,
      text: s.text,
    })),
  };
}

/** Builds schema.org Article JSON-LD for evergreen content pages */
export function buildArticleJsonLd(opts: {
  headline: string;
  url: string;
  image: string;
  datePublished: string;
  dateModified: string;
  lang: string;
  articleSection: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: opts.headline,
    url: opts.url,
    image: opts.image,
    datePublished: opts.datePublished,
    dateModified: opts.dateModified,
    inLanguage: opts.lang,
    articleSection: opts.articleSection,
    author: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
    publisher: buildPublisherSnippet(),
    mainEntityOfPage: { '@type': 'WebPage', '@id': opts.url },
  };
}
